import { Fragment, useEffect, useState } from 'react'
import { Outlet, NavLink, Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog, Transition } from '@headlessui/react'
import {
  api,
  OnboardingProgress,
  useVerticalConfig,
  useRealtimeOnboardingProgress, apiErrorDetail } from '@lipaflow/shared'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useAuthStore } from '@lipaflow/shared'
import toast from 'react-hot-toast'

interface OnboardingStep {
  id: string
  label: string
  description: string
  route?: string
}

interface OnboardingConfig {
  steps: OnboardingStep[]
  import_columns: { key: string; label: string; required: boolean; form_data_key: string }[]
}

export default function OnboardingLayout() {
  const { labels } = useVerticalConfig()
  const institutionLabel = labels.institution.toLowerCase()
  const user = useAuthStore((state) => state.user)
  const queryClient = useQueryClient()

  // Rollback state
  const [rollbackTarget, setRollbackTarget] = useState<OnboardingStep | null>(null)

  // Fetch verification status
  const { data: verification } = useQuery<{ status: string }>({
    queryKey: ['verification-status'],
    queryFn: async () => {
      const res = await api.get('/verification/status')
      return res.data
    },
    retry: false,
  })

  const verificationPending = verification?.status &&
    ['pending', 'submitted', 'in_review'].includes(verification.status)

  // Fetch onboarding config from backend
  const { data: config } = useQuery<OnboardingConfig>({
    queryKey: ['onboarding-config'],
    queryFn: async () => {
      const res = await api.get('/onboarding/config')
      return res.data
    },
  })

  // Fetch onboarding progress (initial load only)
  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress'],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    // Removed refetchInterval - using real-time sync instead
  })

  // Real-time sync for onboarding progress
  useRealtimeOnboardingProgress(user?.tenant_id)

  // Ensure onboarding is explicitly started for legacy/manual entry paths.
  const {
    mutate: startOnboarding,
    isPending: isStartingOnboarding,
    isSuccess: hasStartedOnboarding,
  } = useMutation({
    mutationFn: async () => {
      await api.post('/onboarding/progress/start')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] })
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      toast.error(apiErrorDetail(axiosErr) || 'Failed to start onboarding')
    },
  })

  useEffect(() => {
    const needsStartOrRecovery =
      !!progress &&
      (
        progress.status === 'not_started' ||
        (
          progress.current_step === 'accept_invite' &&
          !progress.completed_steps?.accept_invite
        )
      )

    if (!needsStartOrRecovery) {
      return
    }
    if (isStartingOnboarding || hasStartedOnboarding) {
      return
    }
    startOnboarding()
  }, [hasStartedOnboarding, isStartingOnboarding, progress, startOnboarding])

  // Build steps from config or use defaults from vertical config
  const STEPS = config?.steps ?? [
    { id: 'set_institution_details', label: `${labels.institution} Details`, description: 'Name and contacts' },
    { id: 'link_auth_methods', label: 'Sign-In Security', description: 'Link phone sign-in' },
    { id: 'set_paybill', label: 'Get Paid', description: 'Connect your account and go live' },
  ]

  // Calculate which downstream steps will be reset
  const getDownstreamSteps = (stepId: string): string[] => {
    const idx = STEPS.findIndex((s) => s.id === stepId)
    if (idx < 0) return []
    return STEPS.slice(idx + 1)
      .filter((s) => !!completedSteps[s.id])
      .map((s) => s.label)
  }

  const rollbackMutation = useMutation({
    mutationFn: async (stepId: string) => {
      await api.post('/onboarding/progress/rollback-step', {
        step: stepId,
        reason: 'User-initiated rollback',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] })
      toast.success('Step rolled back successfully')
      setRollbackTarget(null)
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      toast.error(apiErrorDetail(axiosErr) || 'Rollback failed')
    },
  })

  const completedSteps = progress?.completed_steps || {}

  if (isLoading || (progress?.status === 'not_started' && isStartingOnboarding)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // If onboarding is complete, redirect to dashboard
  if (progress?.is_complete) {
    return <Navigate to="/" replace />
  }

  const totalSteps = STEPS.length
  const completedCount = STEPS.filter((step) => !!completedSteps[step.id]).length
  const progressPct = Math.round((completedCount / totalSteps) * 100)

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-stone-900">Setup</h1>
            <p className="text-sm text-stone-500">Complete your {institutionLabel} setup</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-stone-500">{progressPct}% complete</div>
            <div className="w-32 h-2 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-500 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {verificationPending && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="flex items-start gap-3 p-3 bg-warning-50 border border-warning-200 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-warning-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-warning-800">
              LipaFlow is reviewing your organisation. You can finish setup meanwhile; you go live once the review is done.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar - Steps */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <nav className="space-y-1">
            {STEPS.map((step, index) => {
              const isComplete = !!completedSteps[step.id]
              const route = `/onboarding/step/${step.id}`

              return (
                <div key={step.id} className="group relative">
                  <NavLink
                    to={route}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-3 rounded-lg',
                        isActive
                          ? 'bg-teal-50 text-teal-700'
                          : isComplete
                          ? 'text-success-700'
                          : 'text-stone-500 hover:bg-stone-100'
                      )
                    }
                  >
                    <div
                      className={clsx(
                        'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
                        isComplete
                          ? 'bg-success-100 text-success-600'
                          : 'bg-stone-200 text-stone-500'
                      )}
                    >
                      {isComplete ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{step.label}</div>
                      <div className="text-xs text-stone-400">{step.description}</div>
                    </div>
                  </NavLink>
                  {isComplete && (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        setRollbackTarget(step)
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded text-stone-400 hover:text-warning-600 hover:bg-warning-50 transition-all"
                      title={`Rollback "${step.label}"`}
                    >
                      <ArrowUturnLeftIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 bg-white rounded-xl shadow-sm p-6 md:p-8 max-w-3xl">
          <Outlet context={{ progress, config }} />
        </div>
      </div>

      {/* Rollback Confirmation Dialog */}
      <Transition appear show={!!rollbackTarget} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setRollbackTarget(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title className="text-lg font-semibold text-stone-900">
                    Rollback &ldquo;{rollbackTarget?.label}&rdquo;?
                  </Dialog.Title>
                  <div className="mt-3">
                    {rollbackTarget && getDownstreamSteps(rollbackTarget.id).length > 0 ? (
                      <>
                        <p className="text-sm text-stone-600 mb-2">
                          This will also reset the following completed steps:
                        </p>
                        <ul className="list-disc list-inside text-sm text-stone-700 space-y-1 mb-3">
                          {getDownstreamSteps(rollbackTarget.id).map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="text-sm text-stone-600 mb-3">
                        This step will be marked as incomplete so you can redo it.
                      </p>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      onClick={() => setRollbackTarget(null)}
                      className="px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => rollbackTarget && rollbackMutation.mutate(rollbackTarget.id)}
                      disabled={rollbackMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-warning-600 hover:bg-warning-700 rounded-lg disabled:opacity-50"
                    >
                      {rollbackMutation.isPending ? 'Rolling back...' : 'Rollback'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
